/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-02 16:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Icon, TextStyle, Link, FormLayout } from '@shopify/polaris';
import { Field } from 'redux-form';
import { ExternalMinor } from '@shopify/polaris-icons';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';

import {
  SHOW_BUTTON_OPTIONS,
  ICON_POSITION_OPTIONS,
  BUTTON_WIDTH_OPTIONS_V2,
  DISPLAY_TYPE_OPTIONS,
  SELECT_OPTIONS_ACTION_OPTIONS,
  DISPLAY_TYPE_VALUE,
  SELECT_OPTIONS_ACTION_VALUE,
  ELEMENTS,
  ELEMENT_KEY_NAME,
  BUTTON_DISPLAY_OPTIONS,
  BUTTON_SHAPE_VALUE
} from 'src/constants/theme-settings';
import {
  BUTTON_ALIGNMENT_OPTIONS_TEXT,
  BUTTON_SHAPE_OPTIONS
} from 'src/constants/theme-settings.jsx';
import { DropdownIndicator, styles } from './react-select-style';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  themeSettingDefaultValue: any,
  themeSettingDefaultThemeValue: any,
  labelTranslation: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ProductAddToCartButtonForm extends Component<ComposedProps> {
  state = {};

  componentWillReceiveProps(nextProps) {
    const { formValues: nextFormValues, updateField } = nextProps;
    const keyFormValues = Object.keys(_.get(nextFormValues, 'setting', {}));
    const keyAddToCartBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
    );
    const displayType = _.get(nextFormValues, `setting.${keyAddToCartBtn}.displayType`, '');
    const buttonShape = _.get(nextFormValues, `setting.${keyAddToCartBtn}.shape`, '');

    if (
      [DISPLAY_TYPE_VALUE.TEXT_ICON, DISPLAY_TYPE_VALUE.TEXT_ONLY].includes(displayType) &&
      [BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShape)
    ) {
      updateField(`setting.${keyAddToCartBtn}.shape`, BUTTON_SHAPE_VALUE.RECTANGLE);
    }

    if (
      displayType === DISPLAY_TYPE_VALUE.ICON_ONLY &&
      ![BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShape)
    ) {
      updateField(`setting.${keyAddToCartBtn}.shape`, BUTTON_SHAPE_VALUE.CIRCLE);
    }
  }

  render() {
    const { i18n, prefix, formValues } = this.props;

    const formattedShowButton = SHOW_BUTTON_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedDisplayType = DISPLAY_TYPE_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedSelectOptionAction = SELECT_OPTIONS_ACTION_OPTIONS.filter(item => {
      if (
        [ELEMENTS.PRODUCT_INFORMATION_BUTTON_1, ELEMENTS.PRODUCT_INFORMATION_BUTTON_2].includes(
          prefix
        )
      ) {
        return item.value !== SELECT_OPTIONS_ACTION_VALUE.QUICK_ADD_TO_CART;
      }
      return true;
    }).map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedIconPosition = ICON_POSITION_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonWidth = BUTTON_WIDTH_OPTIONS_V2.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonAlignment = BUTTON_ALIGNMENT_OPTIONS_TEXT.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonDisplay = BUTTON_DISPLAY_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const keyFormValues = Object.keys(_.get(formValues, 'setting', {}));
    const keyAddToCartBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
    );
    const isEnableAddToCartBtn = _.get(formValues, `setting.${keyAddToCartBtn}.isEnable`);
    const displayType = _.get(formValues, `setting.${keyAddToCartBtn}.displayType`, '');

    const filteredButtonShape = BUTTON_SHAPE_OPTIONS.filter(item => {
      // Text + icon -> remove square and circle
      if (
        [DISPLAY_TYPE_VALUE.TEXT_ICON, DISPLAY_TYPE_VALUE.TEXT_ONLY].includes(displayType) &&
        [BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(item.value)
      ) {
        return false;
      }

      // Icon only -> only shape square and circle
      if (
        displayType === DISPLAY_TYPE_VALUE.ICON_ONLY &&
        ![BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(item.value)
      ) {
        return false;
      }
      return true;
    });

    return (
      <FormLayout>
        <div className="form-section">
          <div>
            <span className="form-link">
              <TextStyle variation="subdued">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.productAddToCartBtn_UserGuide'
                )}
              </TextStyle>{' '}
              <Link url={URL['ProductButtonThemeSetting']} external>
                {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
                <Icon source={ExternalMinor} color="base" />
              </Link>
              <span style={{ color: '#2C6ECB' }}>.</span>
            </span>
          </div>

          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`setting.${keyAddToCartBtn}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productAddToCartBtn_Enable'
              )}
            />
          </div>

          {isEnableAddToCartBtn && (
            <>
              {/* Show button */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.showOn`}
                  component={RenderSelect2}
                  options={formattedShowButton}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Show_Item')}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* Display options */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.showOnHovering`}
                  component={RenderSelect2}
                  options={formattedButtonDisplay}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_DisplayOption_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* "Select options" action onclick */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.action`}
                  component={RenderSelect2}
                  options={formattedSelectOptionAction}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productAddToCartBtn_SelectAction_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <TextStyle variation="subdued">
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_SelectOptionAction_Note'
                    )}
                  </TextStyle>
                </div>
              </div>

              {/* Display Type */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.displayType`}
                  component={RenderSelect2}
                  options={formattedDisplayType}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
                {displayType === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                  <div style={{ marginTop: '4px' }}>
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Note'
                      )}
                    </TextStyle>
                  </div>
                )}
              </div>

              {/* Icon position */}
              {displayType && displayType === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <div className="title">
                    <span>
                      {i18n.translate(
                        'AdminHTML.ThemeSetting.Form.button_Overall_IconPosition_Item'
                      )}
                    </span>
                  </div>
                  <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered">
                    {formattedIconPosition.map((item, index) => (
                      <Field
                        key={index}
                        name={`setting.${keyAddToCartBtn}.iconPosition`}
                        id={item.value}
                        component={RenderRadioButton2}
                        label={item.label}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Button Shape */}
              <div className="form-section__item form-section__item--medium-spacing">
                <p className="title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Shape_Item')}
                </p>
                <div className="radio-button-wrapper radio-button-wrapper--large radio-button-wrapper--outline">
                  {filteredButtonShape.map((item, index) => (
                    <Field
                      key={index}
                      name={`setting.${keyAddToCartBtn}.shape`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.icon}
                      fullSize={item.fullSize}
                      isShowIconCheck
                    />
                  ))}
                </div>
              </div>

              {/* Button width */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.buttonWidth`}
                  component={RenderSelect2}
                  options={formattedButtonWidth}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.button_Dimension_ButtonWidth_Item'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* Alignment */}
              <div className="form-section__item form-section__item--medium-spacing">
                <p className="title">
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonAlignment_Title'
                  )}
                </p>
                <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--no-radius-middle">
                  {formattedButtonAlignment.map((item, index) => (
                    <Field
                      key={index}
                      name={`setting.${keyAddToCartBtn}.alignment`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.label}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductAddToCartButtonForm);
